import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { getCurrentUser } from "../../lib/authSession";

export function getUserInitials(user) {
  const name = (user?.full_name || "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return `${first}${last}`.toUpperCase();
  }
  const email = (user?.email || "").trim();
  return email ? email[0].toUpperCase() : "?";
}

export function getUserFirstName(user) {
  const name = (user?.full_name || "").trim();
  if (name) return name.split(/\s+/)[0];
  const email = (user?.email || "").trim();
  return email ? email.split("@")[0] : "";
}

/** Small hook: current user snapshot, refreshed on login/logout/impersonation. */
export function useCurrentUser() {
  const [user, setUser] = useState(() => getCurrentUser());

  useEffect(() => {
    const refresh = () => setUser(getCurrentUser());
    window.addEventListener("storage", refresh);
    window.addEventListener("repman:session-changed", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("repman:session-changed", refresh);
    };
  }, []);

  return user;
}

/**
 * Sidebar user profile block — initials avatar + name + email, links to settings.
 *
 * @param {{ profilePath: string }} props
 */
const UserProfileBadge = ({ profilePath }) => {
  const user = useCurrentUser();

  if (!user) return null;

  const displayName = (user.full_name || "").trim() || user.email || "";
  const companyName = user.company?.company_name || "";

  return (
    <NavLink
      to={profilePath}
      className={({ isActive }) =>
        [
          "flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors",
          isActive ? "bg-white shadow-[0_2px_8px_rgba(15,23,42,0.06)]" : "hover:bg-white/60",
        ].join(" ")
      }
    >
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold text-white"
        style={{ background: "linear-gradient(135deg, #13A697 0%, #045C54 100%)" }}
        aria-hidden
      >
        {getUserInitials(user)}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[13px] font-semibold leading-tight text-[#0f172a]">
          {displayName}
        </span>
        <span className="mt-0.5 block truncate text-[11px] leading-tight text-[#94a3b8]">
          {companyName || user.email}
        </span>
      </span>
    </NavLink>
  );
};

export default UserProfileBadge;
